import { createContext, useCallback, useContext, useState } from 'react';

import { isObj } from 'lib/object';

const toMappedOption = (text) => ({ text, value: text });

const AutoCompleteContext = createContext({
  activeOptionIndex: { getter: null, setter: () => {} },
  currInputValue: { getter: '', setter: () => {} },
  maxOptions: { getter: -1, setter: () => {} },
  optionsValuesStore: { getter: { global: [] }, setter: () => {} },
  utils: { getter: null, setter: () => {} },
  getInlineValues: () => {},
  getOptionIndex: () => {},
  optionsMapper: () => {},
});

function AutoCompleteProvider({ children }) {
  /**
   * These fns are dependency of `AutoCompleteContainer` to be mounted, but are
   * used in other components and in this case the `React.cloneElement` don't
   * look like a good solution.
   */
  const [utils, setUtils] = useState({
    onSend: () => {},
    onOptionClick: () => {},
    onResetStates: () => {},
  });
  const [activeOptionIndex, setActiveOptionIndex] = useState(null);
  const [maxOptions, setMaxOptions] = useState(-1);
  const [optionsValuesStore, setOptionsValuesStore] = useState({ global: [] });
  /**
   * TODO the responsibility of create this state can be moved to the component
   * that implements the <AutoComplete />
   */
  const [currInputValue, setCurrInputValue] = useState('');

  const getInlineValues = useCallback(
    () =>
      Object.values(optionsValuesStore).reduce(
        (prev, curr) => [...prev, ...curr],
        []
      ),
    [optionsValuesStore]
  );

  const getOptionIndex = useCallback(
    (value) => {
      const values = getInlineValues();

      return values.findIndex((option) => option === value);
    },
    [optionsValuesStore]
  );

  const optionsMapper = useCallback((options, maxOptionsQty) => {
    if (options.length === 0) {
      return [];
    }

    const option = options[0];

    if (typeof option === 'string') {
      if (maxOptionsQty && maxOptionsQty > 0) {
        return options.map(toMappedOption).slice(0, maxOptionsQty);
      }

      return options.map(toMappedOption);
    }

    if (isObj(option) && 'text' in option && 'value' in option) {
      if (maxOptionsQty && maxOptionsQty > 0) {
        return options.slice(0, maxOptionsQty);
      }

      return options;
    }

    return [];
  }, []);

  return (
    <AutoCompleteContext.Provider
      value={{
        activeOptionIndex: {
          getter: activeOptionIndex,
          setter: setActiveOptionIndex,
        },
        currInputValue: { getter: currInputValue, setter: setCurrInputValue },
        maxOptions: { getter: maxOptions, setter: setMaxOptions },
        optionsValuesStore: {
          getter: optionsValuesStore,
          setter: setOptionsValuesStore,
        },
        utils: { getter: utils, setter: setUtils },
        getInlineValues,
        getOptionIndex,
        optionsMapper,
      }}
    >
      {children}
    </AutoCompleteContext.Provider>
  );
}

const useAutoComplete = () => useContext(AutoCompleteContext);

export default AutoCompleteProvider;

export { useAutoComplete };
